// Last-active org for surfaces that sit outside `/o/:slug` (tray recents, recorder default).
// The web shell's current org is the one whose home/library/admin you're on (`/o/:slug/...`);
// we also persist that slug so the tray can keep fetching the right library after you leave
// an org page (e.g. while recording at `/record`). Falls back to the first org only when
// nothing is set yet.

const STORAGE_KEY = "grackle.lastOrgSlug";

export function getLastOrgSlug(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setLastOrgSlug(slug: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, slug);
  } catch {
    // Locked-down storage: tray just falls back to first-org.
  }
}

/** Slug from an `/o/:slug/...` pathname, or null when the path isn't org-scoped. */
export function orgSlugFromPath(pathname: string): string | null {
  const m = pathname.match(/^\/o\/([^/]+)/);
  return m?.[1] ?? null;
}

export type OrgRef = { id: string; slug: string };

/**
 * Pick the org tray (and similar) should use:
 * 1. the org whose route is currently open (`/o/:slug`),
 * 2. else the last-active slug persisted from a previous org visit,
 * 3. else the first org in the session list.
 */
export function resolveActiveOrg<T extends OrgRef>(
  orgs: T[],
  routeSlug: string | null,
): T | null {
  if (orgs.length === 0) return null;
  if (routeSlug) {
    const fromRoute = orgs.find((o) => o.slug === routeSlug);
    if (fromRoute) return fromRoute;
  }
  const last = getLastOrgSlug();
  if (last) {
    const fromLast = orgs.find((o) => o.slug === last);
    if (fromLast) return fromLast;
  }
  return orgs[0];
}

/** Query flag that marks an explicit "Switch organization" visit to the org chooser. Without it,
 * `/orgs` is only reached by Back / history / a generic "home" link, and a user who already has
 * an active org is sent on to that org's start page instead of the chooser. */
export const SWITCH_ORG_PARAM = "switch";

/** The org chooser, opened deliberately (profile menu / sidebar org card). */
export const SWITCH_ORG_PATH = `/orgs?${SWITCH_ORG_PARAM}=1`;

/** The org the user last worked in, if it is still one of theirs; null when they have never
 * picked one (or lost access to it). Unlike `resolveActiveOrg` there is no first-org fallback:
 * the chooser must still appear for someone who has not chosen yet. */
export function lastActiveOrg<T extends OrgRef>(orgs: T[]): T | null {
  const last = getLastOrgSlug();
  if (!last) return null;
  return orgs.find((o) => o.slug === last) ?? null;
}
